import * as React from "react";
import { Outlet } from "react-router-dom";
import { FileText, Send, User, Mail, Gamepad2, MessageCircle, X, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { SiteTopBar } from "@/components/layout/SiteTopBar";
import { DownloadDock } from "@/components/download/DownloadDock";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { SmokeEffect } from "@/components/effects/SmokeEffect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export const AppLayout = () => {
  const { backgroundImage, backgroundSize, smokeEffectEnabled } = useStore();
  const [requestModalOpen, setRequestModalOpen] = React.useState(false);
  const [requestForm, setRequestForm] = React.useState({
    name: "",
    email: "",
    gameName: "",
    message: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmitRequest = async () => {
    if (!requestForm.name.trim()) {
      toast.error("İsim gerekli");
      return;
    }
    if (!requestForm.email.trim()) {
      toast.error("E-posta gerekli");
      return;
    }
    if (!requestForm.gameName.trim()) {
      toast.error("Oyun adı gerekli");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/contact", {
        firstName: requestForm.name.trim(),
        lastName: "",
        email: requestForm.email.trim(),
        message: `[OYUN TALEBİ: ${requestForm.gameName.trim()}]\n\n${requestForm.message.trim() || "Ek mesaj yok."}`,
      });
      toast.success("Talebiniz gönderildi! En kısa sürede değerlendireceğiz.");
      setRequestModalOpen(false);
      setRequestForm({ name: "", email: "", gameName: "", message: "" });
    } catch {
      toast.error("Talep gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  // Background style - FIXED, content scrolls over it
  const bgStyle = backgroundImage ? {
    backgroundImage: `url(${backgroundImage.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${backgroundImage}` : backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  } : undefined;

  return (
    <div className="min-h-screen bg-background text-foreground" style={bgStyle}>
      <div className="pointer-events-none fixed inset-0 opacity-[0.06] mix-blend-overlay [background-image:var(--noise)]" />

      <div className="relative mx-auto w-full max-w-[1400px] px-2 sm:px-3 md:px-6 pb-20 sm:pb-28 pt-2 sm:pt-3 md:pt-6">
        {/* LEFT MENU REMOVED as requested */}
        <div className="min-w-0">
          <SiteTopBar
            onOpenRequestForm={() => setRequestModalOpen(true)}
            onSearch={(q) => {
              // Forward search to StorePage (rendered via Outlet) without coupling.
              window.dispatchEvent(new CustomEvent("store:search", { detail: q }));
            }}
          />

          <main
            className={cn(
              "mt-3 sm:mt-4 min-w-0",
              "rounded-xl sm:rounded-2xl border border-white/10 glass-effect",
              "p-3 sm:p-4 md:p-6"
            )}
          >
            <MaintenanceGate>
              <Outlet />
            </MaintenanceGate>
          </main>
        </div>
      </div>

      <DownloadDock />
      
      {/* Smoke Effect - Sitenin alt kısmında */}
      {smokeEffectEnabled && <SmokeEffect />}

      {/* Request Form Modal */}
      {requestModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border/60 bg-popover p-5 sm:p-6 shadow-xl animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-500 text-white">
                  <Gamepad2 className="size-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Oyun Talep Formu</h3>
                  <p className="text-xs text-muted-foreground">İstediğiniz oyunu bize bildirin</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-full"
                onClick={() => setRequestModalOpen(false)}
              >
                <X className="size-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1.5">
                  <User className="size-3" />
                  Adınız *
                </label>
                <Input
                  value={requestForm.name}
                  onChange={(e) => setRequestForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Adınızı girin"
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1.5">
                  <Mail className="size-3" />
                  E-posta *
                </label>
                <Input
                  type="email"
                  value={requestForm.email}
                  onChange={(e) => setRequestForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1.5">
                  <Gamepad2 className="size-3" />
                  Talep Edilen Oyun *
                </label>
                <Input
                  value={requestForm.gameName}
                  onChange={(e) => setRequestForm(p => ({ ...p, gameName: e.target.value }))}
                  placeholder="Oyun adını yazın"
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1.5">
                  <MessageCircle className="size-3" />
                  Ek Mesaj (opsiyonel)
                </label>
                <Textarea
                  value={requestForm.message}
                  onChange={(e) => setRequestForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Varsa eklemek istediğiniz notlar..."
                  className="min-h-[80px] rounded-xl"
                />
              </div>
            </div>
            
            <div className="mt-5 flex gap-2">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => setRequestModalOpen(false)}
              >
                İptal
              </Button>
              <Button
                variant="hero"
                className="flex-1 gap-2"
                disabled={submitting}
                onClick={handleSubmitRequest}
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                Gönder
              </Button>
            </div>
            
            <p className="mt-4 text-[10px] text-muted-foreground text-center">
              Talebiniz en kısa sürede değerlendirilecektir. Teşekkürler!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
